import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import {
  Bell, BellOff, Clock, AlertCircle, Trophy, Zap, Sparkles, Crown,
  Bot, CheckCircle2, ChevronRight, X, Flame, Target,
} from 'lucide-react';
import { Button } from './ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const SOURCE_CONFIG = {
  aries: { label: 'Aries V2', icon: Sparkles, color: 'text-amber-400', bg: 'bg-amber-500/15 border-amber-500/30' },
  '7ia': { label: '7IA', icon: Zap, color: 'text-cyan-400', bg: 'bg-cyan-500/15 border-cyan-500/30' },
  'bot-diario': { label: 'Bot Diario', icon: Bot, color: 'text-green-400', bg: 'bg-green-500/15 border-green-500/30' },
  'power-number': { label: 'Poderoso', icon: Crown, color: 'text-orange-400', bg: 'bg-orange-500/15 border-orange-500/30' },
};

const WinCard = ({ win, onMarkRead }) => {
  const config = SOURCE_CONFIG[win.source] || SOURCE_CONFIG['bot-diario'];
  const Icon = config.icon;
  const matchCount = win.match_count || 0;

  return (
    <div
      className={`relative p-3 rounded-lg border transition-all ${
        win.read
          ? 'bg-slate-800/30 border-slate-800/50 opacity-70'
          : 'bg-slate-800/60 border-slate-700/60 hover:border-slate-600'
      }`}
      data-testid={`win-notification-${win.id}`}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border ${config.bg}`}>
          <Icon size={16} className={config.color} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className={`text-xs font-bold ${config.color}`}>{config.label}</span>
            <Badge className={`text-[9px] px-1.5 py-0 ${
              matchCount >= 3 ? 'bg-green-500/20 text-green-300 border-green-500/40' :
              matchCount === 2 ? 'bg-amber-500/20 text-amber-300 border-amber-500/40' :
              'bg-slate-700/50 text-slate-400 border-slate-600/50'
            }`}>
              {matchCount} acierto{matchCount !== 1 ? 's' : ''}
            </Badge>
            {!win.read && <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />}
          </div>
          <p className="text-white text-sm font-medium truncate">{win.lottery_name}</p>
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            {win.winning_numbers?.map((n, i) => {
              const isMatch = win.matched_numbers?.includes(n);
              return (
                <span
                  key={`wn-${n}-${i}`}
                  className={`inline-flex items-center justify-center w-7 h-7 rounded-md text-xs font-bold ${
                    isMatch
                      ? 'bg-green-500/30 text-green-300 border border-green-500/50 ring-1 ring-green-400/30'
                      : 'bg-slate-700/60 text-slate-500 border border-slate-700'
                  }`}
                >
                  {String(n).padStart(2, '0')}
                </span>
              );
            })}
            <span className="text-slate-600 text-[9px] ml-1">resultado</span>
          </div>
          {win.predicted_numbers && (
            <div className="flex items-center gap-1 mt-1 flex-wrap">
              <span className="text-slate-600 text-[9px] mr-0.5">pred:</span>
              {win.predicted_numbers.map((n, i) => {
                const isMatch = win.matched_numbers?.includes(n);
                return (
                  <span
                    key={`pred-${n}-${i}`}
                    className={`text-[10px] font-mono ${isMatch ? 'text-green-400 font-bold' : 'text-slate-500'}`}
                  >
                    {String(n).padStart(2, '0')}{i < win.predicted_numbers.length - 1 ? ',' : ''}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* Mark read */}
        {!win.read && (
          <button
            onClick={(e) => { e.stopPropagation(); onMarkRead(win.id); }}
            className="text-slate-600 hover:text-slate-400 p-1 flex-shrink-0"
            title="Marcar como leida"
          >
            <CheckCircle2 size={14} />
          </button>
        )}
      </div>
      <p className="text-slate-600 text-[9px] mt-1 ml-12">{win.draw_date}</p>
    </div>
  );
};

const NotificationBell = () => {
  const { token } = useAuth();
  const [tab, setTab] = useState('alerts');
  const [wins, setWins] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [upcoming, setUpcoming] = useState([]);
  const [hotAlerts, setHotAlerts] = useState([]);
  const [preDrawAlerts, setPreDrawAlerts] = useState([]);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [permissionState, setPermissionState] = useState('default');
  const notifiedAlerts = React.useRef(new Set());

  const fetchWins = useCallback(async () => {
    if (!token) return;
    try {
      const [winsRes, countRes] = await Promise.all([
        axios.get(`${API}/notifications/wins`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/notifications/wins/unread-count`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setWins(winsRes.data.wins || []);
      setUnreadCount(countRes.data.unread || 0);
    } catch (e) {
      /* silent */
    }
  }, [token]);

  const fetchUpcoming = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/notifications/upcoming`, { headers: { Authorization: `Bearer ${token}` } });
      setUpcoming(res.data.upcoming_draws || []);
    } catch (e) {
      /* silent */
    }
  }, [token]);

  const fetchHotAlerts = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/accuracy/hot-alerts`, { headers: { Authorization: `Bearer ${token}` } });
      setHotAlerts(res.data.alerts || []);
    } catch (e) {
      /* silent */
    }
  }, [token]);

  const fetchPreDrawAlerts = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/notifications/pre-draw-alerts`, { headers: { Authorization: `Bearer ${token}` } });
      const alerts = res.data.alerts || [];
      setPreDrawAlerts(alerts);

      // Send browser notification for new unread pre-draw alerts
      if (Notification.permission === 'granted') {
        for (const alert of alerts) {
          if (!alert.read && !notifiedAlerts.current.has(alert.id)) {
            notifiedAlerts.current.add(alert.id);
            new Notification(`NumeroMaestro - Loteria Caliente`, {
              body: `${alert.lottery_name} (${alert.draw_time}) - Terminal en ${alert.terminal_rate}% de ${alert.sample_size || 'las'} selecciones históricas verificadas. Sorteo en ${alert.minutes_until} min`,
              icon: '/logo192.png',
              tag: alert.id,
            });
          }
        }
      }
    } catch (e) {
      /* silent */
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchWins();
      fetchUpcoming();
      fetchHotAlerts();
      fetchPreDrawAlerts();
      setIsSubscribed(localStorage.getItem('notificationsEnabled') === 'true');
      if ('Notification' in window) {
        setPermissionState(Notification.permission);
      }
      const iv = setInterval(() => {
        fetchWins(); fetchUpcoming(); fetchHotAlerts(); fetchPreDrawAlerts();
      }, 60000);
      return () => clearInterval(iv);
    }
  }, [token, fetchWins, fetchUpcoming, fetchHotAlerts, fetchPreDrawAlerts]);

  useEffect(() => {
    if ('Notification' in window) setPermissionState(Notification.permission);
  }, []);

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/wins/${id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setWins(prev => prev.map(w => w.id === id ? { ...w, read: true } : w));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) { /* silent */ }
  };

  const markPreDrawRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/pre-draw-alerts/${id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setPreDrawAlerts(prev => prev.map(a => a.id === id ? { ...a, read: true } : a));
    } catch (e) { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/wins/read-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setWins(prev => prev.map(w => ({ ...w, read: true })));
      setUnreadCount(0);
    } catch (e) { /* silent */ }
  };

  const toggleNotifications = async () => {
    setLoading(true);
    try {
      if (!isSubscribed) {
        let hasPermission = permissionState === 'granted';
        if (!hasPermission && 'Notification' in window) {
          const perm = await Notification.requestPermission();
          setPermissionState(perm);
          hasPermission = perm === 'granted';
        }
        if (hasPermission) {
          await axios.post(`${API}/notifications/subscribe`, {
            endpoint: `browser-${Date.now()}`, p256dh: 'browser-key', auth: 'browser-auth',
          }, { headers: { Authorization: `Bearer ${token}` } });
          localStorage.setItem('notificationsEnabled', 'true');
          setIsSubscribed(true);
          toast.success('Notificaciones activadas');
        }
      } else {
        await axios.delete(`${API}/notifications/unsubscribe`, { headers: { Authorization: `Bearer ${token}` } });
        localStorage.setItem('notificationsEnabled', 'false');
        setIsSubscribed(false);
        toast.success('Notificaciones desactivadas');
      }
    } catch (e) {
      toast.error('Error al cambiar notificaciones');
    } finally {
      setLoading(false);
    }
  };

  const upcomingSoon = upcoming.filter(n => n.minutes_until <= 30 && n.minutes_until > 0);
  const unreadPreDraw = preDrawAlerts.filter(a => !a.read);
  const totalBadge = unreadCount + upcomingSoon.length + hotAlerts.length + unreadPreDraw.length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative text-slate-400 hover:text-white"
          data-testid="notification-bell"
        >
          {isSubscribed ? <Bell size={20} /> : <BellOff size={20} />}
          {totalBadge > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full text-[10px] flex items-center justify-center text-white font-bold animate-pulse">
              {totalBadge > 9 ? '9+' : totalBadge}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-96 bg-slate-900 border-slate-700 p-0"
        data-testid="notification-popover"
      >
        {/* Header */}
        <div className="p-3 border-b border-slate-700">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-white font-semibold text-sm">Notificaciones</h3>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-slate-500">{isSubscribed ? 'ON' : 'OFF'}</span>
              <Switch
                checked={isSubscribed}
                onCheckedChange={toggleNotifications}
                disabled={loading}
                data-testid="notification-toggle"
              />
            </div>
          </div>
          {/* Tabs */}
          <div className="flex gap-1">
            <button
              onClick={() => setTab('alerts')}
              className={`flex-1 py-1.5 px-2 rounded-md text-xs font-medium transition-all ${
                tab === 'alerts'
                  ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                  : 'text-slate-500 hover:text-slate-300 border border-transparent'
              }`}
              data-testid="tab-alerts"
            >
              <Flame size={12} className="inline mr-1" />
              Calientes {hotAlerts.length > 0 && <span className="ml-1 text-[9px] bg-orange-500/30 px-1.5 rounded-full">{hotAlerts.length}</span>}
            </button>
            <button
              onClick={() => setTab('wins')}
              className={`flex-1 py-1.5 px-2 rounded-md text-xs font-medium transition-all ${
                tab === 'wins'
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                  : 'text-slate-500 hover:text-slate-300 border border-transparent'
              }`}
              data-testid="tab-wins"
            >
              <Trophy size={12} className="inline mr-1" />
              Aciertos {unreadCount > 0 && <span className="ml-1 text-[9px] bg-green-500/30 px-1.5 rounded-full">{unreadCount}</span>}
            </button>
            <button
              onClick={() => setTab('upcoming')}
              className={`flex-1 py-1.5 px-2 rounded-md text-xs font-medium transition-all ${
                tab === 'upcoming'
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                  : 'text-slate-500 hover:text-slate-300 border border-transparent'
              }`}
              data-testid="tab-upcoming"
            >
              <Clock size={12} className="inline mr-1" />
              Sorteos {upcomingSoon.length > 0 && <span className="ml-1 text-[9px] bg-blue-500/30 px-1.5 rounded-full">{upcomingSoon.length}</span>}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="max-h-80 overflow-y-auto">
          {tab === 'alerts' && (
            <>
              {/* Pre-draw alerts (urgent) */}
              {preDrawAlerts.length > 0 && (
                <div className="p-2 space-y-2 border-b border-orange-500/20">
                  <div className="flex items-center gap-1 px-1">
                    <Zap size={12} className="text-yellow-400" />
                    <span className="text-yellow-400 text-[10px] font-bold uppercase tracking-wider">Sorteo Proximo</span>
                  </div>
                  {preDrawAlerts.map((alert) => (
                    <button
                      key={alert.id}
                      type="button"
                      onClick={() => !alert.read && markPreDrawRead(alert.id)}
                      className={`p-3 rounded-lg border transition-all ${
                        alert.read
                          ? 'bg-slate-800/30 border-slate-700/30'
                          : 'bg-yellow-500/10 border-yellow-500/30 animate-pulse'
                      } w-full text-left`}
                      data-testid={`pre-draw-${alert.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Zap size={14} className="text-yellow-400" />
                        <span className="text-white text-sm font-bold">{alert.lottery_name}</span>
                        <span className="text-yellow-400 text-[10px] font-bold ml-auto">
                          {alert.minutes_until} min
                        </span>
                      </div>
                      <div className="text-[10px] text-slate-400">
                        Terminal en muestra histórica: <strong className="text-cyan-400">{alert.terminal_rate}%</strong>
                        {alert.sample_size && <span> ({alert.sample_size} selecciones verificadas)</span>}
                        <span className="mx-1">|</span>
                        Sorteo a las {alert.draw_time}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {hotAlerts.length > 0 ? (
                <div className="p-2 space-y-2">
                  {hotAlerts.map((alert, idx) => (
                    <div
                      key={alert.lottery_id}
                      className={`p-3 rounded-lg border transition-all ${
                        alert.level === 'hot'
                          ? 'bg-orange-500/10 border-orange-500/30 hover:border-orange-400/50'
                          : 'bg-amber-500/10 border-amber-500/20 hover:border-amber-400/40'
                      }`}
                      data-testid={`hot-alert-${idx}`}
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <Flame size={14} className={alert.level === 'hot' ? 'text-orange-400' : 'text-amber-400'} />
                        <span className="text-white text-sm font-bold">{alert.lottery_name}</span>
                        {alert.draw_time && (
                          <span className="text-slate-500 text-[10px]">{alert.draw_time}</span>
                        )}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {alert.reasons.map((r, i) => (
                          <span
                            key={i}
                            className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                              r.includes('Terminal') ? 'bg-cyan-500/20 text-cyan-300' :
                              r.includes('Vecino') ? 'bg-amber-500/20 text-amber-300' :
                              'bg-emerald-500/20 text-emerald-300'
                            }`}
                          >
                            {r}
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-3 mt-2 text-[10px] text-slate-500">
                        <span>T: <strong className="text-cyan-400">{alert.terminal_rate}%</strong></span>
                        <span>V: <strong className="text-amber-400">{alert.neighbor_rate}%</strong></span>
                        <span>E: <strong className="text-emerald-400">{alert.exact_rate}%</strong></span>
                        <span className="ml-auto">{alert.total_comparisons} sorteos</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <Target size={28} className="mx-auto text-slate-700 mb-2" />
                  <p className="text-slate-400 text-sm">Sin alertas calientes</p>
                  <p className="text-slate-600 text-xs mt-1">
                    Se activan cuando una loteria supera 60% en terminales
                  </p>
                </div>
              )}
            </>
          )}

          {tab === 'wins' && (
            <>
              {wins.length > 0 ? (
                <div className="p-2 space-y-2">
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllRead}
                      className="w-full text-center text-[10px] text-slate-500 hover:text-slate-300 py-1"
                      data-testid="mark-all-read"
                    >
                      Marcar todo como leido
                    </button>
                  )}
                  {wins.map(w => (
                    <WinCard key={w.id} win={w} onMarkRead={markRead} />
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <Trophy size={28} className="mx-auto text-slate-700 mb-2" />
                  <p className="text-slate-400 text-sm">Sin aciertos aun</p>
                  <p className="text-slate-600 text-xs mt-1">
                    Cuando tus predicciones acierten, aparecera aqui
                  </p>
                </div>
              )}
            </>
          )}

          {tab === 'upcoming' && (
            <>
              {upcomingSoon.length > 0 ? (
                <div className="p-2 space-y-1">
                  {upcomingSoon.map((draw, idx) => (
                    <div
                      key={draw.lottery_id || `up-${idx}`}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors"
                    >
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center text-white"
                        style={{ backgroundColor: draw.color || '#22c55e' }}
                      >
                        <Clock size={16} />
                      </div>
                      <div className="flex-1">
                        <p className="text-white text-sm font-medium">{draw.lottery_name}</p>
                        <p className="text-slate-400 text-xs">{draw.company}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-green-400 font-mono text-sm font-bold">{draw.minutes_until} min</p>
                        <p className="text-slate-500 text-xs">{draw.draw_time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <Clock size={28} className="mx-auto text-slate-700 mb-2" />
                  <p className="text-slate-400 text-sm">No hay sorteos proximos</p>
                  <p className="text-slate-600 text-xs mt-1">Te avisaremos 15 min antes</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {permissionState === 'denied' && (
          <div className="p-2 border-t border-slate-700 flex items-center gap-2 text-amber-400 text-xs px-3">
            <AlertCircle size={12} />
            Notificaciones bloqueadas en el navegador
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
